/**
 * Customer utility functions for handling Customer_Tambak vs Data_Customer distinctions
 */

import { Customer } from './types';

/**
 * ✅ Safely get customer_tambak_id for database foreign key constraint
 *
 * Returns:
 * - numeric ID if customer is from Customer_Tambak table
 * - null if customer is from Data_Customer table
 *
 * This prevents INVALID_FOREIGN_KEY errors when creating interactions
 * because customer_tambak_id must reference a valid Customer_Tambak.id
 */
export function getCustomerTambakId(customer: Customer | null | undefined): number | null {
  if (!customer) return null;

  // Method 1: Check source_priority field (set during search/transform)
  const sourceCustomer = customer as any;
  if (sourceCustomer.source_priority === 'Data_Customer') {
    return null; // Data_Customer records should not have customer_tambak_id
  }

  // Method 2: Check if idcust starts with 'T' (Customer_Tambak format)
  // Customer_Tambak IDs: T86375577597
  // Data_Customer IDs: DC-... or other formats
  if (customer.idcust?.startsWith('T')) {
    return customer.id;
  }

  // Method 3: Check if customer has NamaPelangganTambak but no Data_Customer indicators
  // This covers cases where source_priority is not set
  if (customer.NamaPelangganTambak && !customer.idcust?.includes('DC-')) {
    return customer.id;
  }

  // Default: assume Data_Customer if uncertain
  return null;
}

/**
 * Get the correct customer identifier for No_pelanggan field
 *
 * Returns the customer's No_pelanggan value (string format)
 */
export function getCustomerNoPelanggan(customer: Customer | null | undefined): string {
  if (!customer) return '';

  // Prefer idcust field (No_pelanggan from database)
  if (customer.idcust) return customer.idcust;

  // Fallback to id as string
  return customer.id?.toString() || '';
}

/**
 * Check if customer is from Customer_Tambak table
 */
export function isCustomerTambak(customer: Customer | null | undefined): boolean {
  return getCustomerTambakId(customer) !== null;
}

/**
 * Check if customer is from Data_Customer table
 */
export function isDataCustomer(customer: Customer | null | undefined): boolean {
  return !isCustomerTambak(customer);
}
